import { memo, useCallback, useRef, useState } from 'react';
import { Tooltip } from '@/components/Tooltip';
import { Dropdownlist } from '@/components/Dropdownlist';
import type { FormFieldType } from '@/services/api-types';
import type { FieldDraft } from './schema-draft';
import { slugLabel } from './schema-draft';

const LAYOUT_ITEMS = [
  { value: 'stacked', label: 'Stacked' },
  { value: 'horizontal', label: 'Horizontal' },
];

const FORMAT_ITEMS = [
  { value: '', label: 'Default' },
  { value: 'currency', label: 'Currency ($)' },
];

const SUMMARY_HELP =
  'Shown on the tour stop summary in Records Search → Tour Stops. Single-value fields only.';

const SEARCHABLE_HELP =
  'Filter submissions by this field\'s value.';

export const ALL_FIELD_TYPES: { value: FormFieldType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Long text' },
  { value: 'date', label: 'Date' },
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
  { value: 'number', label: 'Number' },
  { value: 'radio', label: 'Single choice' },
  { value: 'select', label: 'Dropdown' },
  { value: 'checkboxGroup', label: 'Checkboxes' },
  { value: 'table', label: 'Table' },
  { value: 'computed', label: 'Calculated' },
];

export const SCALAR_FIELD_TYPES: { value: FormFieldType; label: string }[] = ALL_FIELD_TYPES.filter(
  (t) => t.value !== 'table' && t.value !== 'computed',
);

export interface FieldCommonProps {
  field: FieldDraft;
  /** Restricts the type dropdown — used by table-column editor (no nested table/computed). */
  typeOptions?: { value: FormFieldType; label: string }[];
  /** Hide searchable/summary toggles (used for table columns). */
  hideFlags?: boolean;
  onLabelChange: (label: string) => void;
  onKeyChange: (key: string, locked: boolean) => void;
  onTypeChange: (type: FormFieldType) => void;
  onFlagChange: (
    patch: Partial<Pick<FieldDraft,
      'required' | 'searchable' | 'summary' | 'subheading' | 'inlineWithPrevious' | 'wide' | 'format' | 'layout'
    >>,
  ) => void;
  /**
   * Called when the user clicks the "Manage" button next to the searchable checkbox
   * on a persisted field. The parent opens the toggle flow (preview impact + confirm,
   * then call the toggle endpoint). When undefined, the manage action is hidden.
   */
  onRequestSearchableToggle?: (field: FieldDraft) => void;
}

export const FieldCommon = memo(function FieldCommon({
  field,
  typeOptions = ALL_FIELD_TYPES,
  hideFlags = false,
  onLabelChange,
  onKeyChange,
  onTypeChange,
  onFlagChange,
  onRequestSearchableToggle,
}: FieldCommonProps) {
  // Advanced section — collapsed by default. Most admins never need to edit the
  // internal field name; the auto-generated value is fine. Power users can expand.
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const keyInputRef = useRef<HTMLInputElement>(null);

  const handleKeyEdit = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onKeyChange(e.target.value, true);
    },
    [onKeyChange],
  );

  const handleKeyToggle = useCallback(() => {
    if (field.keyLocked) {
      // Revert to auto: recompute the key from the current label and unlock so
      // future label edits keep it in sync.
      onKeyChange(slugLabel(field.label), false);
    } else {
      // Switch to customize: lock the current value, then focus the input so the
      // user can immediately start editing.
      onKeyChange(field.key, true);
      // Defer focus to after the disabled→enabled transition renders.
      setTimeout(() => keyInputRef.current?.focus(), 0);
    }
  }, [field.key, field.label, field.keyLocked, onKeyChange]);

  const supportsSearchable = field.type !== 'table' && field.type !== 'checkboxGroup';
  const supportsSummary = field.type !== 'table';

  return (
    <div className="kst-fb-field-common">
      <div className="kst-fb-row">
        <div className="kstc-form-group kst-fb-cell">
          <label className="kstc-label">Label</label>
          <input
            type="text"
            className="kstc-input"
            value={field.label}
            onChange={(e) => onLabelChange(e.target.value)}
            placeholder="e.g. Start Date"
          />
        </div>
        <div className="kstc-form-group kst-fb-cell kst-fb-cell--narrow">
          <label className="kstc-label">Type</label>
          <Dropdownlist
            value={field.type}
            items={typeOptions}
            sortItems={false}
            searchable={false}
            onChange={(val) => {
              if (typeof val === 'string') onTypeChange(val as FormFieldType);
            }}
          />
        </div>
      </div>

      {!hideFlags && (
        <>
          <div className="kst-fb-flag-section">
            <div className="kst-fb-flag-section-title">Validation</div>
            <div className="kst-fb-flag-row">
              <label className="kst-checkbox-group">
                <input
                  type="checkbox"
                  className="kstc-checkbox"
                  checked={!!field.required}
                  onChange={(e) => onFlagChange({ required: e.target.checked || undefined })}
                />
                <span className="kstc-checkbox-label">Required</span>
              </label>
            </div>
          </div>

          {(supportsSearchable || supportsSummary) && (
            <div className="kst-fb-flag-section">
              <div className="kst-fb-flag-section-title">Where this field appears</div>
              <div className="kst-fb-flag-row">
                {supportsSearchable && (
                  field.isPersisted ? (
                    <div className="kst-checkbox-group">
                      <span className="kstc-checkbox-label">
                        Searchable: <strong>{field.searchable ? 'On' : 'Off'}</strong>
                        <Tooltip content={SEARCHABLE_HELP} position="top" />
                      </span>
                      {onRequestSearchableToggle && (
                        <button
                          type="button"
                          className="kstc-btn kstc-btn--secondary kstc-btn--small"
                          onClick={() => onRequestSearchableToggle(field)}
                          style={{ marginLeft: 8 }}
                          title="Turn this field's filter on or off in Records Search. May take a few seconds."
                        >
                          Change…
                        </button>
                      )}
                    </div>
                  ) : (
                    <label className="kst-checkbox-group">
                      <input
                        type="checkbox"
                        className="kstc-checkbox"
                        checked={!!field.searchable}
                        onChange={(e) => onFlagChange({ searchable: e.target.checked || undefined })}
                      />
                      <span className="kstc-checkbox-label">
                        Searchable <Tooltip content={SEARCHABLE_HELP} position="top" />
                      </span>
                    </label>
                  )
                )}
                {supportsSummary && (
                  <label className="kst-checkbox-group">
                    <input
                      type="checkbox"
                      className="kstc-checkbox"
                      checked={!!field.summary}
                      onChange={(e) => onFlagChange({ summary: e.target.checked || undefined })}
                    />
                    <span className="kstc-checkbox-label">
                      Show on tour stop summary <Tooltip content={SUMMARY_HELP} position="top" />
                    </span>
                  </label>
                )}
              </div>
            </div>
          )}

          <div className="kst-fb-flag-section">
            <div className="kst-fb-flag-section-title">Layout</div>
            <div className="kstc-form-group">
              <label className="kstc-label" htmlFor={`${field.uiId}-subheading`}>
                Subheading above this field <Tooltip content="Renders a small heading and divider above this field. Use to group several fields visually inside one section." position="top" />
              </label>
              <input
                id={`${field.uiId}-subheading`}
                type="text"
                className="kstc-input"
                value={field.subheading ?? ''}
                onChange={(e) => onFlagChange({ subheading: e.target.value || undefined })}
                placeholder="e.g. Key Holder"
              />
            </div>
            <div className="kst-fb-flag-row">
              <label className="kst-checkbox-group">
                <input
                  type="checkbox"
                  className="kstc-checkbox"
                  checked={!!field.inlineWithPrevious}
                  onChange={(e) => onFlagChange({ inlineWithPrevious: e.target.checked || undefined })}
                />
                <span className="kstc-checkbox-label">
                  Show inline with previous field <Tooltip content="Render this field on the same row as the field above instead of a new line." position="top" />
                </span>
              </label>
              <label className="kst-checkbox-group">
                <input
                  type="checkbox"
                  className="kstc-checkbox"
                  checked={!!field.wide}
                  onChange={(e) => onFlagChange({ wide: e.target.checked || undefined })}
                />
                <span className="kstc-checkbox-label">
                  Full width <Tooltip content="Span both columns of the section grid instead of taking a single column." position="top" />
                </span>
              </label>
            </div>
            <div className="kst-fb-row">
              <div className="kstc-form-group kst-fb-cell kst-fb-cell--narrow">
                <label className="kstc-label">
                  Row layout <Tooltip content="Stacked = label above value (default). Horizontal = label on the left, value on the right (good for billing/summary rows)." position="top" />
                </label>
                <Dropdownlist
                  value={field.layout ?? 'stacked'}
                  items={LAYOUT_ITEMS}
                  sortItems={false}
                  searchable={false}
                  onChange={(val) =>
                    onFlagChange({
                      layout: val === 'horizontal' ? 'horizontal' : undefined,
                    })
                  }
                />
              </div>
              {(field.type === 'computed' || field.type === 'number') && (
                <div className="kstc-form-group kst-fb-cell kst-fb-cell--narrow">
                  <label className="kstc-label">
                    Display format <Tooltip content="How the value is shown. 'Currency' formats numbers as $X.XX (US dollars)." position="top" />
                  </label>
                  <Dropdownlist
                    value={field.format ?? ''}
                    items={FORMAT_ITEMS}
                    sortItems={false}
                    searchable={false}
                    onChange={(val) => {
                      const v = typeof val === 'string' ? val : '';
                      onFlagChange({ format: v || undefined });
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <div className="kst-fb-advanced">
        <button
          type="button"
          className="kst-fb-advanced-toggle"
          onClick={() => setAdvancedOpen((v) => !v)}
          aria-expanded={advancedOpen}
        >
          {advancedOpen ? '▾' : '▸'} Advanced
        </button>
        {advancedOpen && (
          <div className="kst-fb-advanced-body">
            <div className="kstc-form-group kst-fb-cell kst-fb-cell--medium">
              <label className="kstc-label">
                Internal name <Tooltip content="System identifier for this field. Auto-generated from the label." position="top" />
              </label>
              <div className="kst-fb-key-row">
                <input
                  ref={keyInputRef}
                  type="text"
                  className="kstc-input"
                  value={field.key}
                  onChange={handleKeyEdit}
                  placeholder="Generated from the label"
                  readOnly={!field.keyLocked}
                  aria-readonly={!field.keyLocked}
                  data-keymode={field.keyLocked ? 'custom' : 'auto'}
                />
                <button
                  type="button"
                  className="kstc-btn kstc-btn--secondary kst-fb-key-lock"
                  onClick={handleKeyToggle}
                  title={field.keyLocked
                    ? 'Revert to auto-generated (follows the label).'
                    : 'Enter a custom value (stops following the label).'}
                >
                  {field.keyLocked ? 'Revert to auto' : 'Customize'}
                </button>
              </div>
              <div className="kst-fb-key-mode">
                {field.keyLocked
                  ? 'Custom. Independent of the label.'
                  : 'Auto. Follows the label.'}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});
